/**
 * Public engine API — the clean, immutable-ish reducer surface.
 *
 * Every reducer:
 *   1. deep-clones the incoming RunState (so callers keep old references),
 *   2. constructs an Rng from run.rng,
 *   3. runs the relevant combat/map logic (which mutates the clone),
 *   4. writes the RNG state back into the clone,
 *   5. returns { state, events }.
 *
 * This is the ONLY layer external code (renderer, AI bot, tests) should call.
 */

import { Rng } from "./rng";
import {
  endTurn as combatEndTurn,
  getRunnerBlock,
  playCard as combatPlayCard,
  startCombat,
} from "./combat";
import { availableNodes, generateMap, isLegalMove, resolveEncounterFor } from "./map";
import { newTraceState, addTrace } from "./trace";
import { getCharacterDef } from "./content/characters";
import { getCardDef, rewardCardIds } from "./content/cards";
import { getIceDef } from "./content/ice";
import { rewardProgramIds, bossProgramIds } from "./content/programs";
import { getEventDef } from "./content/events";
import { heatModifiers } from "./content/ascension";
import { generateShop, removalPrice } from "./shop";
import { cloneRun } from "./state";
import type {
  CardInstance,
  CharacterId,
  CombatEvent,
  EngineResult,
  MapNode,
  RewardState,
  RunState,
} from "./types";

export const CURRENT_SAVE_VERSION = 2;

/**
 * Deterministic run identifier. Same (seed, character, heat) -> same id, so it's
 * reproducible/shareable and stable across save round-trips. Pure FNV-1a hash of
 * the canonical triple — no RNG, no wall-clock, so determinism is preserved.
 */
export function runIdFor(
  seed: string,
  character: CharacterId,
  heat: number,
): string {
  const key = `${seed}|${character}|${Math.max(0, Math.floor(heat))}`;
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return "run_" + (h >>> 0).toString(16).padStart(8, "0");
}

// ---------------------------------------------------------------------------
// New run
// ---------------------------------------------------------------------------

/**
 * Create a fresh run from a seed + character (+ optional Heat tier).
 * Deterministic: same args => byte-identical RunState. Generates act 1's map
 * but does not enter a node yet.
 */
export function newRun(
  seed: string,
  character: CharacterId,
  heat = 0,
): RunState {
  const rng = Rng.fromSeed(seed);
  const charDef = getCharacterDef(character);
  const heatMods = heatModifiers(heat);

  let nextUid = 1;
  const deck: CardInstance[] = charDef.startingDeck.map((defId) => ({
    uid: nextUid++,
    defId,
  }));
  // High Heat seeds the deck with a Corruption curse.
  if (heatMods.startWithCurse) {
    deck.push({ uid: nextUid++, defId: "corruption" });
  }

  const maxHp = Math.max(1, charDef.maxHp - heatMods.hpPenalty);

  // Generate act 1 map from the SAME rng (so map is part of the seeded stream).
  const map = generateMap(1, rng);

  const run: RunState = {
    version: CURRENT_SAVE_VERSION,
    runId: runIdFor(seed, character, heat),
    seed,
    character,
    heat: Math.max(0, Math.floor(heat)),
    rng: rng.getState(),
    phase: "map",
    hp: maxHp,
    maxHp,
    gold: charDef.startingGold,
    deck,
    programs: charDef.startingProgram ? [charDef.startingProgram] : [],
    nextUid,
    map,
    combat: null,
    trace: null,
    stats: {
      damageDealt: 0,
      damageTaken: 0,
      cardsPlayed: 0,
      enemiesKilled: 0,
      elitesKilled: 0,
      combatsWon: 0,
      turnsTaken: 0,
      maxTraceReached: 0,
    },
    pendingReward: null,
    pendingEvent: null,
    floor: 0,
    _runnerStatuses: {},
  };
  return run;
}

/** Event id pool (stable, sorted) for deterministic event selection. */
const EVENT_ID_POOL: string[] = [
  "black_market",
  "data_cache",
  "med_bay",
  "purge_station",
  "rogue_ai",
  "signal_ghost",
];

// ---------------------------------------------------------------------------
// Map navigation
// ---------------------------------------------------------------------------

/**
 * Move to a map node and resolve its kind. For combat/elite/boss nodes this
 * starts a fight (sets run.phase = "combat"). Other node kinds set the matching
 * phase for the caller to resolve (event/shop/rest/treasure handled in later
 * phases; here we at least mark them entered).
 */
export function chooseMapNode(state: RunState, nodeId: string): EngineResult {
  const run = cloneRun(state);
  const events: CombatEvent[] = [];

  if (run.phase !== "map") {
    throw new Error(`chooseMapNode requires phase "map", got "${run.phase}"`);
  }
  if (!isLegalMove(run.map, nodeId)) {
    throw new Error(`Illegal move to node ${nodeId}`);
  }
  const node = run.map.nodes[nodeId];
  if (!node) throw new Error(`No such node ${nodeId}`);

  run.map.currentNodeId = nodeId;
  if (!run.map.visited.includes(nodeId)) run.map.visited.push(nodeId);
  run.floor += 1;

  const rng = new Rng(run.rng);

  const heatMods = heatModifiers(run.heat);

  switch (node.kind) {
    case "combat":
    case "elite":
    case "boss": {
      const enemyDefIds = resolveEncounterFor(run.map.act, node, rng);
      // Heat tunes the Trace clock for this combat (program mods stack on top
      // inside startCombat).
      const trace = newTraceState(
        Math.max(1, newTraceState().max + heatMods.traceMaxDelta),
        Math.max(0, newTraceState().perTurn + heatMods.tracePerTurnDelta),
      );
      const ev = startCombat(run, enemyDefIds, rng, trace);
      events.push(...ev);
      // Apply Heat enemy-HP multipliers post-spawn (keeps combat.ts Heat-free).
      applyHeatToEnemies(run, heatMods);
      // High Heat: begin the fight already partially traced.
      if (heatMods.startingTrace > 0 && run.trace) {
        run.trace = addTrace(run.trace, heatMods.startingTrace, "heat").trace;
      }
      run.phase = "combat";
      break;
    }
    case "rest":
      run.phase = "rest";
      break;
    case "event": {
      // Pick a random event for this node deterministically.
      run.pendingEvent = pickEvent(rng);
      run.phase = "event";
      break;
    }
    case "shop":
      // Generate the shop stock deterministically the first time it's entered.
      run.shop = generateShop(run, rng);
      run.phase = "shop";
      break;
    case "treasure": {
      run.phase = "reward";
      // Treasure: gold + a guaranteed Program choice (the relic the player came
      // for), scaled by Heat's gold multiplier.
      run.pendingReward = {
        gold: scaleGold(rng.int(15, 30), heatMods),
        cardChoices: [],
        programChoice: rng.pick(rewardProgramIds()),
        taken: false,
      };
      break;
    }
    default: {
      const _exhaustive: never = node.kind;
      throw new Error(`Unhandled node kind ${_exhaustive}`);
    }
  }

  run.rng = rng.getState();
  return { state: run, events };
}

/** Apply Heat enemy/elite HP multipliers to the freshly-spawned ICE. */
function applyHeatToEnemies(
  run: RunState,
  heatMods: ReturnType<typeof heatModifiers>,
): void {
  if (!run.combat) return;
  if (heatMods.enemyHpMult === 1 && heatMods.eliteHpMult === 1) return;
  for (const ice of run.combat.enemies) {
    const def = getIceDef(ice.defId);
    let mult = heatMods.enemyHpMult;
    if (def.isElite || def.isBoss) mult *= heatMods.eliteHpMult;
    const scaled = Math.max(1, Math.round(ice.maxHp * mult));
    ice.maxHp = scaled;
    ice.hp = scaled;
  }
}

/** Scale a gold amount by Heat's gold multiplier (rounded, floored at 0). */
function scaleGold(
  base: number,
  heatMods: ReturnType<typeof heatModifiers>,
): number {
  return Math.max(0, Math.round(base * heatMods.goldMult));
}

/** Deterministically pick an event id for an event node. */
function pickEvent(rng: Rng): string {
  // Lazy import-free: the event registry ids are stable + sorted.
  return rng.pick(EVENT_ID_POOL);
}

/** List legal next nodes (for UI + AI). */
export function getAvailableNodes(state: RunState): MapNode[] {
  return availableNodes(state.map);
}

// ---------------------------------------------------------------------------
// Combat reducers
// ---------------------------------------------------------------------------

export function playCard(
  state: RunState,
  handIndex: number,
  targetUid?: number,
): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "combat" || !run.combat) {
    throw new Error("playCard requires an active combat");
  }
  const events: CombatEvent[] = [];
  const rng = new Rng(run.rng);
  combatPlayCard(run, handIndex, targetUid, rng, events);
  run.rng = rng.getState();
  postCombatTransition(run);
  return { state: run, events };
}

export function endTurn(state: RunState): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "combat" || !run.combat) {
    throw new Error("endTurn requires an active combat");
  }
  const events: CombatEvent[] = [];
  const rng = new Rng(run.rng);
  // Count the turn taken.
  run.stats.turnsTaken += 1;
  combatEndTurn(run, rng, events);
  run.rng = rng.getState();
  postCombatTransition(run);
  return { state: run, events };
}

/**
 * After any combat action, check for win/loss and transition run.phase + grant
 * post-combat rewards. Keeps combat.ts focused on the fight only.
 */
function postCombatTransition(run: RunState): void {
  const combat = run.combat;
  if (!combat) return;
  if (combat.phase === "won") {
    const node = run.map.currentNodeId
      ? run.map.nodes[run.map.currentNodeId]
      : undefined;
    const isBoss = node?.kind === "boss";
    // Clean up combat state but keep stats.
    run.combat = null;
    run.trace = null;
    run._runnerStatuses = {};
    const rng = new Rng(run.rng);
    if (isBoss) {
      // Boss down: grant a BOSS Program reward, then advance act or win.
      run.pendingReward = rollBossReward(run, rng);
      run.rng = rng.getState();
      run.phase = "reward";
    } else {
      // Grant a reward to resolve before returning to the map.
      run.pendingReward = rollCombatReward(run, rng);
      run.rng = rng.getState();
      run.phase = "reward";
    }
  } else if (combat.phase === "lost") {
    run.combat = null;
    run.trace = null;
    run.phase = "dead";
  }
}

function rollCombatReward(run: RunState, rng: Rng): RewardState {
  const node = run.map.currentNodeId
    ? run.map.nodes[run.map.currentNodeId]
    : undefined;
  const elite = node?.kind === "elite";
  const heatMods = heatModifiers(run.heat);
  const gold = scaleGold(elite ? rng.int(25, 40) : rng.int(10, 25), heatMods);
  // Offer 3 distinct card choices from the reward pool.
  const pool = rewardCardIds();
  const choices: string[] = [];
  const bag = rng.shuffle(pool);
  for (const id of bag) {
    if (choices.length >= 3) break;
    choices.push(id);
  }
  // Elites always grant a Program; normal combats have a chance.
  const programChoice =
    elite || rng.chance(0.15) ? rng.pick(rewardProgramIds()) : null;
  return { gold, cardChoices: choices, programChoice, taken: false };
}

function rollBossReward(run: RunState, rng: Rng): RewardState {
  const heatMods = heatModifiers(run.heat);
  const gold = scaleGold(rng.int(50, 75), heatMods);
  // A boss grants a powerful boss Program + a card choice.
  const programChoice = rng.pick(bossProgramIds());
  const pool = rewardCardIds();
  const choices: string[] = [];
  const bag = rng.shuffle(pool);
  for (const id of bag) {
    if (choices.length >= 3) break;
    choices.push(id);
  }
  return { gold, cardChoices: choices, programChoice, taken: false, isBoss: true };
}

// ---------------------------------------------------------------------------
// Reward / rest resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a pending reward.
 *  - `cardDefId`: a card from `cardChoices` to add (or null to skip).
 *  - `takeProgram`: if true and a programChoice is offered, claim the relic.
 * Boss rewards additionally advance the act (or win the run) once taken.
 */
export function takeReward(
  state: RunState,
  cardDefId: string | null,
  takeProgram = true,
): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "reward" || !run.pendingReward) {
    throw new Error("takeReward requires a pending reward");
  }
  const reward = run.pendingReward;
  if (reward.taken) throw new Error("Reward already taken");

  run.gold += reward.gold;
  if (cardDefId) {
    if (!reward.cardChoices.includes(cardDefId)) {
      throw new Error(`Card ${cardDefId} not offered`);
    }
    getCardDef(cardDefId); // validate
    run.deck.push({ uid: run.nextUid++, defId: cardDefId });
  }
  if (takeProgram && reward.programChoice) {
    // Don't grant duplicates; just skip if already owned.
    if (!run.programs.includes(reward.programChoice)) {
      run.programs.push(reward.programChoice);
    }
  }

  const wasBoss = reward.isBoss === true;
  reward.taken = true;
  run.pendingReward = null;

  if (wasBoss) {
    if (run.map.act >= 3) {
      run.phase = "victory";
    } else {
      const rng = new Rng(run.rng);
      run.map = generateMap(run.map.act + 1, rng);
      run.rng = rng.getState();
      run.phase = "map";
    }
  } else {
    run.phase = "map";
  }
  return { state: run, events: [] };
}

/** Rest node: heal (Heat-scaled) % max HP. */
export function rest(state: RunState): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "rest") throw new Error("rest requires phase rest");
  const heatMods = heatModifiers(run.heat);
  const heal = Math.max(
    0,
    Math.floor(run.maxHp * 0.3 * heatMods.restHealMult),
  );
  run.hp = Math.min(run.maxHp, run.hp + heal);
  run.phase = "map";
  return { state: run, events: [] };
}

// ---------------------------------------------------------------------------
// Shop resolution
// ---------------------------------------------------------------------------

/**
 * Buy a card from the shop by its slot id. Validates funds + availability,
 * deducts gold, adds the card to the deck, and marks the slot purchased.
 */
export function buyShopCard(state: RunState, slot: string): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "shop" || !run.shop) {
    throw new Error("buyShopCard requires an active shop");
  }
  const item = run.shop.cards.find((c) => c.slot === slot);
  if (!item) throw new Error(`No shop card slot ${slot}`);
  if (item.purchased) throw new Error("Card already purchased");
  if (run.gold < item.price) throw new Error("Not enough gold");
  getCardDef(item.defId); // validate
  run.gold -= item.price;
  run.deck.push({ uid: run.nextUid++, defId: item.defId });
  item.purchased = true;
  return { state: run, events: [] };
}

/** Buy a Program from the shop by its slot id. */
export function buyShopProgram(state: RunState, slot: string): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "shop" || !run.shop) {
    throw new Error("buyShopProgram requires an active shop");
  }
  const item = run.shop.programs.find((p) => p.slot === slot);
  if (!item) throw new Error(`No shop program slot ${slot}`);
  if (item.purchased) throw new Error("Program already purchased");
  if (run.gold < item.price) throw new Error("Not enough gold");
  if (run.programs.includes(item.defId)) {
    // Defensive: shop never stocks owned programs, but guard anyway.
    throw new Error("Program already owned");
  }
  run.gold -= item.price;
  run.programs.push(item.defId);
  item.purchased = true;
  return { state: run, events: [] };
}

/**
 * Use the card-removal service: pay the current price and remove `cardUid` from
 * the deck. Price climbs with each use this shop.
 */
export function buyShopRemove(
  state: RunState,
  cardUid: number,
): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "shop" || !run.shop) {
    throw new Error("buyShopRemove requires an active shop");
  }
  const price = removalPrice(run.shop);
  if (run.gold < price) throw new Error("Not enough gold");
  const idx = run.deck.findIndex((c) => c.uid === cardUid);
  if (idx < 0) throw new Error(`No card with uid ${cardUid} in deck`);
  run.gold -= price;
  run.deck.splice(idx, 1);
  run.shop.remove.used += 1;
  return { state: run, events: [] };
}

/** Leave the shop and return to the map. Clears the shop stock. */
export function leaveShop(state: RunState): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "shop") throw new Error("leaveShop requires phase shop");
  run.shop = null;
  run.phase = "map";
  return { state: run, events: [] };
}

// ---------------------------------------------------------------------------
// Event resolution
// ---------------------------------------------------------------------------

/**
 * Resolve an event choice. Applies the choice's declarative outcomes through the
 * run RNG (random card/program rolls are seeded). `removeCardUid` is required if
 * the chosen outcome includes a "removeCard". Returns to the map.
 */
export function resolveEvent(
  state: RunState,
  choiceId: string,
  removeCardUid?: number,
): EngineResult {
  const run = cloneRun(state);
  if (run.phase !== "event" || !run.pendingEvent) {
    throw new Error("resolveEvent requires an active event");
  }
  const def = getEventDef(run.pendingEvent);
  const choice = def.choices.find((c) => c.id === choiceId);
  if (!choice) throw new Error(`No such event choice ${choiceId}`);
  if (choice.requiresGold != null && run.gold < choice.requiresGold) {
    throw new Error("Not enough gold for that choice");
  }
  if (choice.requiresProgram && run.programs.length === 0) {
    throw new Error("That choice requires a Program");
  }

  const rng = new Rng(run.rng);
  if (choice.costGold) run.gold = Math.max(0, run.gold - choice.costGold);

  for (const outcome of choice.outcomes) {
    switch (outcome.kind) {
      case "gold":
        run.gold = Math.max(0, run.gold + (outcome.value ?? 0));
        break;
      case "heal":
        run.hp = Math.min(run.maxHp, run.hp + (outcome.value ?? 0));
        break;
      case "loseHp":
        run.hp = Math.max(1, run.hp - (outcome.value ?? 0)); // events never kill
        break;
      case "maxHp": {
        const delta = outcome.value ?? 0;
        run.maxHp = Math.max(1, run.maxHp + delta);
        if (delta > 0) run.hp = Math.min(run.maxHp, run.hp + delta);
        else run.hp = Math.min(run.hp, run.maxHp);
        break;
      }
      case "gainCard":
        if (outcome.cardDefId) {
          getCardDef(outcome.cardDefId);
          run.deck.push({ uid: run.nextUid++, defId: outcome.cardDefId });
        }
        break;
      case "gainRandomCard":
        run.deck.push({
          uid: run.nextUid++,
          defId: rng.pick(rewardCardIds()),
        });
        break;
      case "gainProgram": {
        const pid = rng.pick(rewardProgramIds());
        if (!run.programs.includes(pid)) run.programs.push(pid);
        break;
      }
      case "removeCard":
        if (removeCardUid != null) {
          const idx = run.deck.findIndex((c) => c.uid === removeCardUid);
          if (idx >= 0) run.deck.splice(idx, 1);
        }
        break;
      case "addCurse":
        run.deck.push({ uid: run.nextUid++, defId: "corruption" });
        break;
      case "addTrace":
      case "nothing":
        break;
      default: {
        const _exhaustive: never = outcome.kind;
        throw new Error(`Unhandled event outcome ${_exhaustive}`);
      }
    }
  }

  run.rng = rng.getState();
  run.pendingEvent = null;
  run.phase = "map";
  return { state: run, events: [] };
}

/** Upgrade a card in the deck (used by rest/smith). Replaces def with upgrade. */
export function upgradeCard(state: RunState, cardUid: number): EngineResult {
  const run = cloneRun(state);
  const card = run.deck.find((c) => c.uid === cardUid);
  if (!card) throw new Error(`No card with uid ${cardUid} in deck`);
  const def = getCardDef(card.defId);
  if (!def.upgradesTo) throw new Error(`Card ${def.id} cannot be upgraded`);
  card.defId = def.upgradesTo;
  return { state: run, events: [] };
}

// ---------------------------------------------------------------------------
// Read-only selectors (renderer/AI conveniences)
// ---------------------------------------------------------------------------

export { getRunnerBlock };

export function isRunOver(state: RunState): boolean {
  return state.phase === "dead" || state.phase === "victory";
}
